/**
 * Serialize / deserialize instructor org course update example previews
 * (progress % + gradebook cells) for storage on update events.
 */

function toPercent (value) {
    if (value == null || value === '') return null
    const n = Number(value)
    if (!Number.isFinite(n)) return null
    return Math.max(0, Math.min(100, Math.round(n)))
}

function toTrimmedString (value) {
    if (value == null) return ''
    return String(value).trim()
}

function normalizeGradebookColumn (raw, index) {
    const fallbackIndex = index + 1
    if (!raw || typeof raw !== 'object') {
        return {
            id: `module-${index}`,
            index: fallbackIndex,
            title: `Module ${fallbackIndex}`,
            shortLabel: `Mod ${fallbackIndex}`,
        }
    }
    return {
        id: toTrimmedString(raw.id) || `module-${index}`,
        index: Number(raw.index) || fallbackIndex,
        title: toTrimmedString(raw.title) || `Module ${fallbackIndex}`,
        shortLabel: toTrimmedString(raw.shortLabel ?? raw.short_label) || `Mod ${fallbackIndex}`,
    }
}

/**
 * @param {unknown} side
 * @returns {{ columns: Array<{ id: string, index: number, title: string, shortLabel: string }>, cells: string[], overall: string } | null}
 */
export function serializeGradebookSide (side) {
    if (!side || typeof side !== 'object') return null
    const columns = Array.isArray(side.columns)
        ? side.columns.map((col, index) => normalizeGradebookColumn(col, index))
        : []
    const cells = Array.isArray(side.cells)
        ? side.cells.map((cell) => (cell == null ? '--' : String(cell)))
        : []
    const overall = toTrimmedString(side.overall) || '--'
    if (!columns.length && !cells.length) return null
    return { columns, cells, overall }
}

/**
 * @param {unknown} side
 */
function normalizeStoredGradebookSide (side) {
    const serialized = serializeGradebookSide(side)
    return serialized || { columns: [], cells: [], overall: '--' }
}

/**
 * Build API payload from computed preview values at update time.
 *
 * @param {{ beforePercent?: number | null, afterPercent?: number | null, gradebookPreview?: { before?: unknown, after?: unknown } | null }} params
 */
export function buildExampleUpdatePreviewPayload ({
    beforePercent,
    afterPercent,
    gradebookPreview,
} = {}) {
    const beforePercentComplete = toPercent(beforePercent)
    const afterPercentComplete = toPercent(afterPercent)
    if (beforePercentComplete == null || afterPercentComplete == null) {
        return null
    }

    const preview = gradebookPreview && typeof gradebookPreview === 'object' ? gradebookPreview : null
    const gradebookBefore = serializeGradebookSide(preview?.before)
    const gradebookAfter = serializeGradebookSide(preview?.after)

    const payload = {
        beforePercentComplete,
        afterPercentComplete,
    }
    if (gradebookBefore) payload.gradebookBefore = gradebookBefore
    if (gradebookAfter) payload.gradebookAfter = gradebookAfter
    return payload
}

/**
 * @param {Record<string, unknown> | null | undefined} rawEvent
 */
export function normalizeExampleUpdatePreview (rawEvent) {
    const raw = rawEvent && typeof rawEvent === 'object' ? rawEvent : {}
    const source = raw.exampleUpdatePreview
        ?? raw.example_update_preview
        ?? null
    if (!source || typeof source !== 'object') return null

    const beforePercentComplete = toPercent(
        source.beforePercentComplete ?? source.before_percent_complete
    )
    const afterPercentComplete = toPercent(
        source.afterPercentComplete ?? source.after_percent_complete
    )
    if (beforePercentComplete == null || afterPercentComplete == null) {
        return null
    }

    const gradebookBeforeRaw = source.gradebookBefore ?? source.gradebook_before
    const gradebookAfterRaw = source.gradebookAfter ?? source.gradebook_after

    return {
        beforePercentComplete,
        afterPercentComplete,
        gradebookBefore: gradebookBeforeRaw
            ? normalizeStoredGradebookSide(gradebookBeforeRaw)
            : null,
        gradebookAfter: gradebookAfterRaw
            ? normalizeStoredGradebookSide(gradebookAfterRaw)
            : null,
    }
}

/**
 * @param {ReturnType<typeof normalizeExampleUpdatePreview> | null | undefined} preview
 */
export function hasUsableExampleUpdatePreview (preview) {
    return (
        preview != null
        && preview.beforePercentComplete != null
        && preview.afterPercentComplete != null
    )
}

/**
 * Shape expected by CourseVersionUpdateExampleTabs gradebook props.
 *
 * @param {ReturnType<typeof normalizeExampleUpdatePreview>} preview
 */
export function gradebookPreviewFromStoredExample (preview) {
    if (!preview) return null
    const before = preview.gradebookBefore
    const after = preview.gradebookAfter
    if (!before && !after) return null
    return {
        before: before || { columns: [], cells: [], overall: '--' },
        after: after || { columns: [], cells: [], overall: '--' },
    }
}
